import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function LoginDialog() {
  const { loginOpen, closeLogin, sendCode, confirmCode } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("email");
    setCode("");
    setBusy(false);
  };

  const onClose = (open) => {
    if (!open) {
      closeLogin();
      setTimeout(reset, 200);
    }
  };

  const onSend = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const r = await sendCode(email.trim());
      toast.success("Code sent", { description: r.message });
      setStep("code");
    } catch (err) {
      toast.error("Could not send code", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const u = await confirmCode(email.trim(), code.trim());
      toast.success(`Signed in as ${u.email}`);
      closeLogin();
      setTimeout(reset, 200);
    } catch (err) {
      toast.error("Verification failed", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={loginOpen} onOpenChange={onClose}>
      <DialogContent className="rounded-sm border-slate-200 sm:max-w-md" data-testid="login-dialog">
        <DialogHeader>
          <div className="mb-1 grid h-11 w-11 place-items-center rounded-sm bg-slate-900 text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">
            {step === "email" ? "Sign in to get estimates" : "Enter your code"}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {step === "email"
              ? "We'll email you a 6-digit code. You get 3 free estimates each month."
              : `We sent a 6-digit code to ${email}. It expires in 10 minutes.`}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <form onSubmit={onSend} className="flex flex-col gap-3">
            <Label htmlFor="login-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Email address
            </Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="login-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 rounded-sm border-slate-300 pl-9 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid="login-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="h-12 rounded-sm bg-slate-900 font-semibold text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
              data-testid="login-send-code-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              Send code
            </Button>
          </form>
        ) : (
          <form onSubmit={onVerify} className="flex flex-col gap-3">
            <Label htmlFor="login-code" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              6-digit code
            </Label>
            <Input
              id="login-code"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••"
              className="h-12 rounded-sm border-slate-300 text-center font-mono-plex text-2xl tracking-[0.5em] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              data-testid="login-code-input"
            />
            <Button
              type="submit"
              disabled={busy}
              className="h-12 rounded-sm bg-slate-900 font-semibold text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
              data-testid="login-verify-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Verify & continue"}
            </Button>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="mt-1 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900"
              data-testid="login-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Use a different email
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
